import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';

/* The design's "⋯" menu: the less frequent actions about one thing, behind one button.
 *
 * **A disclosure with menu items, keyboard-complete.** Escape closes and returns focus to the button,
 * a click outside closes, and the arrow keys walk the items — the three things a menu that is only
 * clicked never needs and a keyboard user cannot do without. An item that is absent for this reader
 * (no permission) is simply not passed, so the menu never offers a door that will slam.
 *
 * `danger` items sit last with a rule above them, so a destructive action is never the first thing a
 * keyboard lands on and never adjacent to the action a reader presses most.
 */

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly danger?: boolean;
  readonly disabled?: boolean;
}

export function OverflowMenu({
  label,
  items,
}: {
  /** The button's accessible name, e.g. "More actions". */
  readonly label: string;
  readonly items: readonly MenuItem[];
}): ReactElement | null {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent): void => {
      if (root.current !== null && !root.current.contains(event.target as Node))
        setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    root.current
      ?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')
      ?.focus();
    return () => {
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  if (items.length === 0) return null;
  const plain = items.filter((item) => item.danger !== true);
  const danger = items.filter((item) => item.danger === true);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      button.current?.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const all = Array.from(
      root.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]:not(:disabled)',
      ) ?? [],
    );
    const at = all.findIndex((item) => item === document.activeElement);
    const next =
      all[(at + (event.key === 'ArrowDown' ? 1 : -1) + all.length) % all.length];
    next?.focus();
  };

  const renderItem = (item: MenuItem): ReactElement => (
    <button
      key={item.id}
      type="button"
      role="menuitem"
      className={`lq-menu__item${item.danger === true ? ' lq-menu__item--danger' : ''}`}
      disabled={item.disabled}
      onClick={() => {
        setOpen(false);
        item.onSelect();
      }}
    >
      {item.label}
    </button>
  );

  return (
    <div className="lq-menu" ref={root} onKeyDown={onKeyDown}>
      <button
        ref={button}
        type="button"
        className="lq-menu__button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open ? (
        <div className="lq-menu__list" role="menu" id={menuId} aria-label={label}>
          {plain.map(renderItem)}
          {danger.length > 0 && plain.length > 0 ? (
            <div className="lq-menu__rule" role="separator" />
          ) : null}
          {danger.map(renderItem)}
        </div>
      ) : null}
    </div>
  );
}
